import { useGateValue, useStatsigUser } from '@statsig/react-bindings'
import {
  Company,
  CompanyType,
  FeatureFlagValue,
  UserIdentify,
  UserRoles,
} from '@types'
import { useSelector } from 'stores'
import { useService } from 'services'
import { useQuery } from 'modules/navigation'
import { analyticsMerchant, analyticsSupplier } from 'modules/analytics'
import { Groups, analyticsInstance } from 'lib/events'

export function useUserTarget() {
  const userDetail = useSelector((state) => state.user.detail)
  const auth = useSelector((state) => state.auth)
  const { events } = useService()
  const { query } = useQuery()
  const { user } = useStatsigUser()

  const isNewXpEnabled = useGateValue(FeatureFlagValue.NEW_EXPERIENCE)

  function getUserType(
    company: Company
  ): CompanyType | 'buyer_seller' | undefined {
    if (company.is_buyer && company.is_seller) return 'buyer_seller'
    if (company.is_buyer) return CompanyType.Buyer
    if (company.is_seller) return CompanyType.Seller
    return undefined
  }

  function composeBase() {
    const { is_admin, currentCompany } = userDetail.data
    const type = getUserType(currentCompany)
    return {
      type,
      isAdmin: is_admin,
      isAnonymous: auth.user.data.isAnonymous,
      authId: auth.user.data.uid,
      userId: userDetail.data.id,
    }
  }

  function composeUserWithEmail(email: string) {
    return {
      ...composeBase(),
      email,
    }
  }

  function getTrackerId() {
    const param = query.get('tracker_id') || query.get('trackerId')

    return param || 'webapp'
  }

  function setHotjar() {
    window.hj(
      'identify',
      userDetail.data.id,
      composeUserWithEmail(userDetail.data.email)
    )
  }

  function setUserRole({ is_seller, is_salesperson }: Company): UserRoles {
    if (is_salesperson) return 'salesperson'
    if (is_seller) return 'supplier'
    return 'merchant'
  }

  function setIdentity(data?: UserIdentify) {
    const email = auth.user.data.email || data?.email
    events.target = composeUserWithEmail(email || '')

    const {
      currentCompany,
      id,
      display_name,
      is_admin,
      companies
    } = userDetail.data

    const {
      is_buyer,
      is_salesperson,
      is_seller
    } = currentCompany

    if (!id) return

    // Remove previous groups
    companies?.forEach((company) => {
      analyticsSupplier.removeGroup(Groups.Supplier, company.id)
      analyticsMerchant.removeGroup(Groups.Merchant, company.id)
    })

    const userEventObject = {
      name: display_name,
      email: userDetail.data.email,
      companyID: currentCompany.id,
      isAdmin: is_admin,
      role: setUserRole(currentCompany),
    }

    const supplierInfo = {
      name: currentCompany.name,
      document: currentCompany.document_number,
    }

    // Reset All apps
    if (isNewXpEnabled) {
      analyticsInstance.identify(id, userEventObject)
      analyticsInstance.setGroup(
        Groups.Supplier,
        currentCompany.id,
        supplierInfo
      )
    } else {
      if (is_buyer) {
        analyticsMerchant.identify(id, userEventObject)
        analyticsMerchant.setGroup(
          Groups.Merchant,
          currentCompany.id,
          supplierInfo
        )
      }

      if (is_salesperson || is_seller) {
        analyticsSupplier.identify(id, userEventObject)
        analyticsMerchant.setGroup(
          Groups.Supplier,
          currentCompany.id,
          supplierInfo
        )
      }
    }
  }

  return {
    auth: auth.user,
    user,
    client: userDetail,
    setHotjar,
    setIdentity,
    getTrackerId,
  }
}
